import React, { memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import { SendOutlineIcon } from "modules/icons";
import {
  Text,
  HStack,
  Button,
  Circle,
  VStack,
  IconButton,
} from "@chakra-ui/react";
import { TextAlignment, THREAD_DRAFT_ID } from "modules/utils/Constants";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { useUsers } from "modules/providers/users-provider";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import { useOverlay } from "modules/providers/overlay-provider";
import { DRAWER_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import { MessageThreadType } from "gql/graphql";

interface IProps {
  userId?: string;
  distributionGroupId?: string;
  displayText?: boolean;
  textAlignment?: TextAlignment;
}

const NewMessageThreadButton = ({
  userId,
  distributionGroupId,
  displayText = true,
  textAlignment = "inline",
}: IProps) => {
  const { openDrawer } = useOverlay();
  const { createDraft } = useMessageThreadDraft({
    draftId: THREAD_DRAFT_ID,
  });
  const { usersById } = useUsers();
  const { distributionGroupsById } = useDistributionGroups();

  const user = useMemo(
    () => (userId ? usersById[userId] : undefined),
    [usersById, userId],
  );

  const distributionGroup = useMemo(
    () =>
      distributionGroupId
        ? distributionGroupsById[distributionGroupId]
        : undefined,
    [distributionGroupsById, distributionGroupId],
  );

  const onNewThreadClicked = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      await createDraft(
        {
          type: MessageThreadType.Thread,
          draftType: "new-thread",
          userIds: userId ? [userId] : [],
          distributionGroupIds: distributionGroupId
            ? [distributionGroupId]
            : [],
        },
        true,
      );
      openDrawer({
        type: DRAWER_TYPE.COMPOSE_THREAD,
      });
    },
    [createDraft, distributionGroupId, openDrawer, userId],
  );

  if (user && !user.active) {
    return null;
  }

  if (distributionGroup && !distributionGroup.active) {
    return null;
  }

  if (!displayText) {
    return (
      <IconButton
        onClick={onNewThreadClicked}
        aria-label="New Thread"
        variant="hover"
        bg={colors.orangePrimary}
        rounded="full"
        size="sm"
        icon={<SendOutlineIcon boxSize={6} fill={colors.white} />}
      />
    );
  }

  if (textAlignment === "inline") {
    return (
      <Button
        onClick={onNewThreadClicked}
        aria-label="New Chat"
        variant="hover"
        height="auto"
        px={0}
      >
        <HStack spacing={1}>
          <Circle bg={colors.orangePrimary} size={6}>
            <SendOutlineIcon boxSize={4} fill={colors.white} />
          </Circle>
          <Text
            fontSize={10}
            fontWeight={500}
            color={colors.gray50}
            textAlign="center"
          >
            New Thread
          </Text>
        </HStack>
      </Button>
    );
  }

  return (
    <Button
      onClick={onNewThreadClicked}
      aria-label="New Chat"
      variant="hover"
      height="auto"
      px={0}
    >
      <VStack spacing={1}>
        <Circle bg={colors.orangePrimary} size={6}>
          <SendOutlineIcon boxSize={4} fill={colors.white} />
        </Circle>
        <Text
          fontSize={10}
          fontWeight={500}
          color={colors.gray50}
          textAlign="center"
        >
          New
          <br />
          Thread
        </Text>
      </VStack>
    </Button>
  );
};

export default memo(NewMessageThreadButton);
